import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';

export interface SelectOption {
  readonly value: string | number;
  readonly label: string;
}

export interface CustomSelectProps {
  readonly id?: string;
  readonly value: string | number;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly triggerClassName?: string;
  readonly label?: string;
  readonly icon?: React.ReactNode;
  readonly ariaLabel?: string;
}

export function CustomSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  className = '',
  triggerClassName = '',
  label,
  icon,
  ariaLabel,
}: CustomSelectProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const defaultTriggerClass =
    'flex w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground hover:border-slate-400 focus:outline-none focus:ring-1 focus:ring-[#489B2B] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer shadow-xs transition-colors';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label !== undefined && (
        <label
          htmlFor={id}
          className="mb-1 text-[11px] font-semibold text-slate-400 flex items-center gap-1.5"
        >
          {icon}
          {label}
        </label>
      )}
      {id !== undefined && (
        <select
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          aria-label={ariaLabel ?? label}
          tabIndex={-1}
          className="sr-only"
        >
          {options.map((opt) => (
            <option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      <button
        id={id !== undefined ? `${id}-trigger` : undefined}
        type="button"
        disabled={disabled}
        {...(id === undefined && (ariaLabel !== undefined || label !== undefined)
          ? { 'aria-label': ariaLabel ?? label }
          : {})}
        aria-expanded={isOpen}
        onClick={() => {
          if (!disabled) setIsOpen(!isOpen);
        }}
        className={triggerClassName !== '' ? triggerClassName : defaultTriggerClass}
      >
        <span className="truncate">
          {selectedOption !== undefined ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full min-w-[140px] overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-md text-xs">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-slate-400">No options available</div>
          ) : (
            options.map((option) => {
              const isSelected = String(option.value) === String(value);
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => {
                    onChange(String(option.value));
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-sm px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-[#489B2B]/10 text-[#489B2B] font-bold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <span className="text-[#489B2B] font-bold">✓</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
